/**
 * Atualização automática — sempre ligada.
 *
 * Detecção barata, a cada ~60 s:
 * - o servidor de blocos (`/blocos/v2/ultimos`) diz o horário mais recente que
 *   ele já cortou de cada produto — ele sonda o STAR horário a horário, então
 *   enxerga também quadro publicado atrasado;
 * - um `HEAD` em `<produto base>/latest.jpg` (`cache: 'no-store'`) lê o
 *   `Last-Modified` (cabeçalho liberado pelo CORS). Sozinho ele não basta: em
 *   22/09/2026 o GOES-19 voltou de uma pane soltando os quadros das 12:00–12:20
 *   sem mexer no latest.jpg, e o portal só os via na recarga de 5 min.
 * Quando um dos dois muda — ou, como garantia, a cada 5 min — recarregamos as
 * listagens dos produtos ativos; o controlador desliza a janela (períodos
 * relativos) e o pré-carregamento baixa os quadros novos. Nunca sondamos nomes
 * de arquivo no STAR que talvez não existam (o 404 do CDN vem sem CORS e suja
 * o console). Com a aba oculta, pausa; ao voltar a ficar visível, verifica na hora.
 */
import { frameCache } from '../goes/frameCache';
import { productDirUrl } from '../goes/star';
import { useCatalogStore } from '../goes/catalog';
import { useGoesStore } from '../state/goes';
import { useTimeStore } from '../state/time';
import { useUiStore } from '../state/ui';
import { dismissToast, showToast } from '../state/toasts';
import { formatDateBrt, formatDayTimeBrt, formatTimeBrt } from '../lib/time';
import { parseStarStamp } from '../goes/stamp';
import { BLOCOS_URL, VERSAO_BLOCOS } from '../detalhe/blocos';
import { blocosDisponiveis } from '../detalhe/saude';
import { refreshListings } from './controller';
import { ATRASO_NOAA_MS } from './semNoaa';
import { getProductName } from '../config/products';
import { usePlanStore } from './plan';
import { useLoopStore } from './store';

/** Intervalo do HEAD em latest.jpg. */
export const PROBE_INTERVAL_MS = 60_000;
/** Recarga completa das listagens, mesmo sem mudança detectada. */
export const REFRESH_INTERVAL_MS = 5 * 60_000;

// O limiar mora com a linha do tempo sem a NOAA (semNoaa.ts); daqui ele segue exportado.
export { ATRASO_NOAA_MS } from './semNoaa';
export const AVISO_ATRASO = 'noaa-atrasada';
/** Quadro mais recente para o qual o aviso já foi dado (um por episódio). */
let avisadoPara: number | null = null;

/** Imagem mais recente que a NOAA publicou do produto base (ou null). */
function ultimaPublicada(): number | null {
  const base = useGoesStore.getState().layers[0]?.productId;
  return base ? (useCatalogStore.getState().entries[base]?.listing?.times.at(-1) ?? null) : null;
}

/**
 * Avisa quando a NOAA para de publicar — e tira o aviso sozinho quando ela
 * volta. O AmaView continua conferindo a cada minuto nesse meio tempo.
 */
export function conferirAtrasoDaNoaa(latest: number | null = ultimaPublicada(), now: number = Date.now()): void {
  if (latest === null) return;
  if (now - latest <= ATRASO_NOAA_MS) {
    if (avisadoPara !== null) dismissToast(AVISO_ATRASO);
    avisadoPara = null;
    return;
  }
  if (avisadoPara === latest) return;
  avisadoPara = latest;
  showToast({
    id: AVISO_ATRASO,
    kind: 'warning',
    // Avisa e sai: fixo, ele cobria a lista dos últimos focos. O selo de status
    // continua dizendo "NOAA atrasada" enquanto durar.
    timeoutMs: 15_000,
    // Parada de outro dia (a do GeoColor de 20/10/2026 passou da meia-noite): com a data.
    message: mensagemAtrasoDaNoaa(
      `${formatDateBrt(latest) === formatDateBrt(now) ? formatTimeBrt(latest) : formatDayTimeBrt(latest)} (Brasília)`,
      nomeDaBase(),
    ),
  });
}

/** Nome do produto da camada de base (ou null). */
function nomeDaBase(): string | null {
  const base = useGoesStore.getState().layers[0]?.productId;
  return base ? getProductName(base) : null;
}

/**
 * O mesmo texto no aviso e nas dicas dos selos ("desde …": hora, ou data e hora,
 * já formatadas). Às vezes só um produto para (o GeoColor, em 20/10/2026), e a
 * linha do tempo segue com as outras camadas (semNoaa.ts): o texto diz qual.
 */
export function mensagemAtrasoDaNoaa(desde: string, produto: string | null = null): string {
  // Curto: no celular o aviso cobria por 15 s os botões do trilho de camadas.
  const oQue = produto ? `“${produto}”` : 'imagens novas do GOES-19';
  return `A NOAA não publica ${oQue} desde ${desde}. As outras camadas seguem no horário; o AmaView confere a cada minuto e atualiza sozinho.`;
}

let lastFullRefresh = Date.now();
let lastModified: string | null = null;
let running = false;
const refreshHooks = new Set<() => void>();

/**
 * Registra uma ação extra a cada ciclo de atualização (ex.: os focos revalidam
 * o arquivo diário do INPE). Retorna o cancelamento.
 */
export function registerRefreshHook(hook: () => void): () => void {
  refreshHooks.add(hook);
  return () => {
    refreshHooks.delete(hook);
  };
}

function runHooks(): void {
  for (const hook of refreshHooks) {
    try {
      hook();
    } catch (e) {
      console.warn('[AmaView] Falha numa ação de atualização:', e);
    }
  }
}

/**
 * Verifica agora (quadro novo detectado, recarga periódica, botão
 * "Verificar agora" ou volta da conexão). Retorna true se chegou imagem nova,
 * false se não, e null se já havia uma verificação em andamento.
 *
 * Os ganchos (ex.: focos) rodam DEPOIS das listagens e só se a janela do loop
 * não mudou — quando muda, os módulos já recarregam por conta própria.
 */
export async function checkNow(): Promise<boolean | null> {
  if (running) return null;
  running = true;
  lastFullRefresh = Date.now();
  try {
    frameCache.retryFailed();
    const before = useTimeStore.getState().range;
    const found = await refreshListings();
    // O controlador recalcula a janela num microtask depois da listagem.
    await new Promise((resolve) => setTimeout(resolve, 0));
    const after = useTimeStore.getState().range;
    const rangeChanged = before?.start !== after?.start || before?.end !== after?.end;
    if (!rangeChanged && !useLoopStore.getState().exportLocked) runHooks();
    return found;
  } finally {
    running = false;
  }
}

/** `Last-Modified` do latest.jpg do produto base (ou null se não deu). */
async function probeLatest(): Promise<string | null> {
  const base = useGoesStore.getState().layers[0]?.productId;
  if (!base) return null;
  try {
    const res = await fetch(`${productDirUrl(base)}latest.jpg`, { method: 'HEAD', cache: 'no-store', mode: 'cors', credentials: 'omit' });
    return res.ok ? res.headers.get('last-modified') : null;
  } catch {
    return null;
  }
}

/** Último horário do servidor de blocos que já disparou uma recarga (uma por quadro novo). */
let blocosVisto: number | null = null;

/**
 * Horário mais recente que o servidor de blocos já cortou do produto (ou null
 * se ele está fora do ar). Um JSON de ~1 KB.
 */
async function probeBlocos(productId: string): Promise<number | null> {
  if (!BLOCOS_URL || !blocosDisponiveis()) return null;
  try {
    const res = await fetch(`${BLOCOS_URL}/${VERSAO_BLOCOS}/ultimos`, {
      cache: 'no-store',
      mode: 'cors',
      credentials: 'omit',
      signal: AbortSignal.timeout(8000),
    });
    if (!res.ok) return null;
    const stamp = ((await res.json()) as Record<string, string>)[productId];
    return stamp ? parseStarStamp(stamp) : null;
  } catch {
    return null;
  }
}

/**
 * O servidor de blocos tem quadro mais novo que a listagem na tela? Dispara
 * uma vez por quadro novo (se a listagem ainda não o mostra na recarga, a
 * próxima olhada não insiste).
 */
export function blocosTemNovidade(noServidor: number | null, listado: number | null): boolean {
  if (noServidor === null || noServidor === blocosVisto) return false;
  if (listado !== null && noServidor <= listado) return false;
  blocosVisto = noServidor;
  return true;
}

/** Uma rodada: HEADs baratos; listagens só se mudou (ou a cada 5 min). */
async function tick(): Promise<void> {
  if (document.visibilityState === 'hidden' || !navigator.onLine || running) return;
  const base = useGoesStore.getState().layers[0]?.productId;
  const [lm, noServidor] = await Promise.all([probeLatest(), base ? probeBlocos(base) : Promise.resolve(null)]);
  let changed = blocosTemNovidade(noServidor, ultimaPublicada());
  if (lm) {
    // Na primeira sonda não há referência: o latest.jpg mudou depois da
    // última listagem? (No máximo uma recarga extra se o relógio divergir.)
    if (lastModified === null) {
      const lastCheck = useCatalogStore.getState().lastCheck ?? 0;
      const modified = Date.parse(lm);
      changed ||= Number.isFinite(modified) && modified > lastCheck;
    } else {
      changed ||= lm !== lastModified;
    }
    lastModified = lm;
  }
  if (changed || Date.now() - lastFullRefresh >= REFRESH_INTERVAL_MS) await checkNow();
  conferirAtrasoDaNoaa();
}

export function startAutoRefresh(): () => void {
  lastFullRefresh = Date.now();
  lastModified = null;
  avisadoPara = null;
  blocosVisto = null;
  // A primeira listagem chega logo depois da partida: confere o atraso já nela.
  const pararAtraso = useCatalogStore.subscribe(() => conferirAtrasoDaNoaa());

  const interval = setInterval(() => void tick(), PROBE_INTERVAL_MS);
  const onVisibility = () => {
    if (document.visibilityState === 'visible') void tick();
  };
  const onOnline = () => {
    useUiStore.getState().set({ online: true });
    showToast({ id: 'net', kind: 'success', message: 'Conexão restabelecida. Atualizando…', timeoutMs: 4000 });
    // Recuperação: listagens que falharam offline não reagendaram novas tentativas.
    void checkNow();
  };
  const onOffline = () => {
    useUiStore.getState().set({ online: false });
    // Sem nada na tela, o aviso central do mapa já explica a falta de conexão.
    if (!usePlanStore.getState().firstFrameShown) return;
    showToast({
      id: 'net',
      kind: 'warning',
      message: 'Sem conexão com a internet. Mostrando o que já foi carregado.',
      timeoutMs: 0,
    });
  };

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  if (!navigator.onLine) onOffline();

  return () => {
    pararAtraso();
    clearInterval(interval);
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
  };
}
